import React from 'react';

import { ValueGroup } from '@candu-ai/craft/ui';
import { Section } from 'craft/containers/Toolbox/components';
import { useCss, useMultiNodes } from 'craft/hooks';
import { BeaconColor } from 'craft/resolvers/Hotspot/toolbox/BeaconColor';
import { BeaconSizeStepper } from 'craft/resolvers/Hotspot/toolbox/BeaconSizeStepper';
import { BeaconPosition } from 'craft/resolvers/Hotspot/types';
import { CssSelectorId } from 'craft/types';
import { getDomNodeFromDocument } from 'craft/utils/viewport';

import { BeaconPositionStepper } from './BeaconPositionStepper';
import { HotspotZindex } from './HotspotZindex';

const horizontalPositionOptions = [
  { label: 'Left', value: 'left' },
  { label: 'Right', value: 'right' },
];

interface Props {
  nodeIds: string[];
}

export const BeaconSettings = ({ nodeIds }: Props) => {
  const { css: beaconCss, setCss: setBeaconCss } = useCss(nodeIds, {
    cssSelectorId: CssSelectorId.HotspotBeacon,
  });

  const {
    custom,
    beaconPosition,
    actions: { setCustom },
  } = useMultiNodes(nodeIds, (node) => ({
    custom: node?.data.custom?.custom ?? {},
    beaconPosition: (node?.data.custom?.custom?.beaconPosition ??
      BeaconPosition.Left) as BeaconPosition,
  }));

  const onHorizontalPositionTypeChange = (value: 'left' | 'right') => {
    if (value !== beaconPosition) {
      const nodeId = nodeIds[0];
      const dom = getDomNodeFromDocument(nodeId);
      if (dom) {
        // @ts-ignore
        dom.dataset.refresh = Date.now();
      }

      setCustom({ custom: { ...custom, beaconPosition: value } });

      // if there is a value, assign it to the new property and remove the old one
      if (value === 'right') {
        setBeaconCss({ right: beaconCss.left ?? '0px', left: undefined });
      } else if (value === 'left' && typeof beaconCss.right !== 'undefined') {
        setBeaconCss({ left: beaconCss.right, right: undefined });
      }
    }
  };

  return (
    <Section name="Beacon">
      <ValueGroup>
        <BeaconColor nodeIds={nodeIds} />
      </ValueGroup>

      <ValueGroup>
        <BeaconPositionStepper
          property="top"
          label="Top"
          isInGroup={{ isFirst: true }}
          cssValue={beaconCss.top}
          setValue={(payload) => setBeaconCss(payload)}
        />
        <BeaconPositionStepper
          property={beaconPosition as 'right' | 'left'}
          label={horizontalPositionOptions.find(({ value }) => value === beaconPosition)?.label}
          options={horizontalPositionOptions}
          onPropertyChange={onHorizontalPositionTypeChange}
          isInGroup={{ isLast: true }}
          cssValue={beaconCss[beaconPosition as 'right' | 'left']}
          setValue={(payload) => setBeaconCss(payload)}
        />
      </ValueGroup>

      <ValueGroup>
        <BeaconSizeStepper nodeIds={nodeIds} />
      </ValueGroup>

      <ValueGroup>
        <HotspotZindex nodeIds={nodeIds} />
      </ValueGroup>
    </Section>
  );
};
